import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/WithdrawPage.css';
import { BackgroundStars } from '../components/BackgroundStars';
import { NavigationBar } from '../components/NavigationBar';
import { useGameStore } from '../store/gameStore';
import { giftAPI } from '../services/api';
import { CustomModal } from '../components/CustomModal';

const WITHDRAW_STARS_RATE = 1000;
const SUPPORT_USERNAME = '@StarsRocketSupport';

const gifts = [
  { id: 'bear15', title: 'Мишка', icon: '🧸', stars: 15 },
  { id: 'heart15', title: 'Сердечко', icon: '💖', stars: 15 },
  { id: 'flower25', title: 'Цветок', icon: '🌹', stars: 25 },
  { id: 'rocket50', title: 'Ракета', icon: '🚀', stars: 50 },
  { id: 'gem100', title: 'Алмаз', icon: '💎', stars: 100 },
];

const WithdrawPage: React.FC = () => {
  const { stars, setStars } = useGameStore();
  const navigate = useNavigate();
  const withdrawBalance = stars / WITHDRAW_STARS_RATE;
  const [giftText, setGiftText] = React.useState('');
  const [isBuyingId, setIsBuyingId] = React.useState<string | null>(null);
  const [modalIsOpen, setModalIsOpen] = React.useState(false);
  const [modalConfig, setModalConfig] = React.useState({
    title: '',
    message: '',
    buttons: [] as Array<{ id: string; text: string; type?: string }>,
  });

  const showPopup = (title: string, message: string) => {
    setModalConfig({
      title,
      message,
      buttons: [{ id: 'ok', type: 'default', text: 'Понятно' }],
    });
    setModalIsOpen(true);
  };

  const handleBuyGift = async (gift: { id: string; title: string; stars: number }) => {
    const requiredInternalStars = gift.stars * WITHDRAW_STARS_RATE;
    if (stars < requiredInternalStars) {
      showPopup('Недостаточно звёзд', `Для подарка «${gift.title}» нужно ${requiredInternalStars} ⭐ игры (${gift.stars} ⭐ вывода)`);
      return;
    }

    try {
      setIsBuyingId(gift.id);
      const response = await giftAPI.buyGift({
        giftKey: gift.id,
        text: giftText.trim() || undefined,
      });

      if (!response?.success) {
        showPopup('Ошибка вывода', response?.message || 'Не удалось отправить подарок');
        return;
      }

      if (typeof response.stars === 'number') {
        setStars(response.stars);
      }

      const queueText = response.queueId ? `\nID в очереди: ${response.queueId}` : '';
      showPopup(
        'Подарок отправлен',
        `${response.message || 'Заявка добавлена в очередь'}${queueText}\n\nВажно: чтобы подарок был доставлен, заранее напишите любое сообщение в ${SUPPORT_USERNAME} и добавьте аккаунт в контакты.`,
      );
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.message || 'Сервер временно недоступен';
      showPopup('Ошибка вывода', apiMessage);
    } finally {
      setIsBuyingId(null);
    }
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="app-title">SaperStars</div>
        <div className="app-score">
          <div className="score-group">
            <span className="score-value">{stars.toFixed(1)}</span>
            <span className="score-icon">⭐</span>
          </div>
        </div>
      </header>

      <div className="content withdraw-content">
        <BackgroundStars />

        <section className="withdraw-banner-wrap">
          <button className="withdraw-promo-banner" type="button" onClick={() => navigate('/tasks')}>
            <div className="withdraw-promo-content">
              <div className="withdraw-promo-title">✨ Получи 1000 звёзд Telegram!</div>
              <div className="withdraw-promo-subtitle">Нажми, чтобы узнать как!</div>
            </div>
            <img className="withdraw-promo-image" src="/img/Plush%20Pepe.webp" alt="Plush Pepe" />
          </button>
        </section>

        <section className="withdraw-gifts-panel">
          <h2 className="withdraw-gifts-title">✨ Выбери подарок ✨</h2>
          <div className="withdraw-support-note" role="note">
            <div className="withdraw-support-note-title">Перед выводом важно</div>
            <div className="withdraw-support-note-text">
              Чтобы получить подарок, сначала напишите любое сообщение в <strong>{SUPPORT_USERNAME}</strong>
              {' '}и добавьте аккаунт в контакты.
            </div>
          </div>
          <div className="withdraw-conversion-note">Курс вывода: {WITHDRAW_STARS_RATE} ⭐ игры = 1 ⭐ вывода</div>
          <div className="withdraw-conversion-balance">Доступно для вывода: {withdrawBalance.toFixed(2)} ⭐</div>
          <div className="withdraw-form">
            <input
              className="withdraw-input"
              value={giftText}
              onChange={(e) => setGiftText(e.target.value)}
              placeholder="Сообщение к подарку (опционально)"
            />
          </div>
          <div className="withdraw-gifts-grid">
            {gifts.map((gift) => {
              const requiredInternalStars = gift.stars * WITHDRAW_STARS_RATE;
              const enoughStars = stars >= requiredInternalStars;
              const isBuying = isBuyingId === gift.id;
              return (
                <div key={gift.id} className="withdraw-gift-card">
                  <div className="withdraw-gift-icon">{gift.icon}</div>
                  <div className="withdraw-gift-name">{gift.title}</div>
                  <div className="withdraw-gift-price">⭐ {gift.stars}</div>
                  <div className="withdraw-gift-internal-price">{requiredInternalStars} ⭐ игры</div>
                  <button
                    className="withdraw-gift-button"
                    type="button"
                    disabled={!enoughStars || Boolean(isBuyingId)}
                    onClick={() => handleBuyGift(gift)}
                  >
                    {isBuying ? 'Отправка...' : enoughStars ? 'Вывести' : 'Недостаточно звёзд'}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <NavigationBar />

      <CustomModal
        isOpen={modalIsOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        buttons={modalConfig.buttons}
        onClose={() => setModalIsOpen(false)}
      />
    </div>
  );
};

export default WithdrawPage;
